import React, { useEffect, useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import { DEFAULT_FONT_FAMILY } from '@/constants/config';

const motion = `
.maps-loading { box-sizing: border-box; width: 100%; max-width: 800px; margin: 0 auto; padding: 28px 20px; overflow: auto; min-height: 0; }
.maps-loading-intro { display: flex; align-items: center; gap: 18px; padding: 6px 2px 26px; }
.maps-loading-art { width: 72px; height: 72px; flex-shrink: 0; border-radius: 22px; background: var(--maps-wash); display: grid; place-items: center; overflow: hidden; }
.maps-loading-art svg { width: 64px; height: 64px; overflow: visible; }
.maps-loading-pin { transform-box: fill-box; transform-origin: center bottom; animation: maps-pin-float 3.6s ease-in-out infinite; }
.maps-loading-pin:nth-of-type(2) { animation-delay: .4s; }
.maps-loading-pin:nth-of-type(3) { animation-delay: .8s; }
.maps-loading-title { margin: 0 0 7px; font: inherit; font-size: 19px; font-weight: 600; letter-spacing: -.4px; line-height: 1.35; overflow-wrap: anywhere; }
.maps-loading-note { margin: 0; font-size: 14px; line-height: 1.5; color: var(--maps-muted); }
.maps-loading-cards { display: grid; gap: 12px; }
.maps-loading-card { position: relative; display: flex; gap: 16px; align-items: center; overflow: hidden; border: 1px solid var(--maps-border); border-radius: 18px; padding: 14px; background: var(--maps-card); }
.maps-loading-card:nth-child(2) { opacity: .72; }
.maps-loading-card:nth-child(3) { opacity: .42; }
.maps-loading-photo { width: 80px; height: 86px; flex-shrink: 0; border-radius: 12px; background: var(--maps-wash); }
.maps-loading-lines { flex: 1; display: grid; gap: 11px; }
.maps-loading-line { height: 9px; border-radius: 8px; background: var(--maps-wash); }
.maps-loading-line:first-child { height: 12px; width: 56%; margin-bottom: 2px; }
.maps-loading-line:nth-child(2) { width: 32%; }
.maps-loading-line:nth-child(3) { width: 76%; }
.maps-loading-card::after { content: ''; position: absolute; inset: 0; background: linear-gradient(105deg, transparent 25%, var(--maps-sheen) 48%, transparent 72%); transform: translateX(-100%); animation: maps-card-sheen 2.8s ease-in-out infinite; }
.maps-loading-card:nth-child(2)::after { animation-delay: .12s; }
.maps-loading-card:nth-child(3)::after { animation-delay: .24s; }
@keyframes maps-pin-float { 0%, 70%, 100% { transform: translateY(0); } 35% { transform: translateY(-5px); } }
@keyframes maps-card-sheen { 0%, 15% { transform: translateX(-100%); } 75%, 100% { transform: translateX(100%); } }
@media (max-width: 600px) { .maps-loading { padding-top: 18px; } .maps-loading-intro { gap: 14px; } .maps-loading-art { width: 60px; height: 60px; border-radius: 19px; } .maps-loading-art svg { width: 54px; height: 54px; } .maps-loading-title { font-size: 17px; } }
@media (prefers-reduced-motion: reduce) { .maps-loading-pin, .maps-loading-card::after { animation: none; } .maps-loading-card::after { display: none; } }
`;

export default function MapsLoadingState({ location }) {
  const { theme } = useTheme();
  const p = getCatalogPalette(theme);
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  return <section className="maps-loading" aria-label="Finding places" aria-busy="true" style={{ fontFamily: DEFAULT_FONT_FAMILY, color: p.ink, '--maps-wash': p.preview, '--maps-card': p.control, '--maps-border': p.border, '--maps-muted': p.icon, '--maps-sheen': `${p.onInk}66` }}>
    <style>{motion}</style>
    <div className="maps-loading-intro">
      <div className="maps-loading-art" aria-hidden="true">
        <svg viewBox="0 0 80 80" fill="none">
          <path d="M9 27 28 20 51 27 71 20v35l-20 8-23-7-19 7V27Z" stroke="currentColor" strokeOpacity=".12" strokeWidth="1.5" fill="currentColor" fillOpacity=".025" />
          <path d="M28 20v36m23-29v36M11 45l18-7 23 7 17-7" stroke="currentColor" strokeOpacity=".1" strokeWidth="1.5" />
          <g className="maps-loading-pin"><path d="M22 22a7 7 0 1 0-14 0c0 5 7 11 7 11s7-6 7-11Z" transform="translate(7 8)" fill="currentColor" fillOpacity=".3" /><circle cx="22" cy="30" r="2" fill={p.control} /></g>
          <g className="maps-loading-pin"><path d="M53 20a9 9 0 1 0-18 0c0 7 9 15 9 15s9-8 9-15Z" fill="currentColor" /><circle cx="44" cy="20" r="3" fill={p.control} /></g>
          <g className="maps-loading-pin"><path d="M68 42a6 6 0 1 0-12 0c0 5 6 10 6 10s6-5 6-10Z" fill="currentColor" fillOpacity=".5" /><circle cx="62" cy="42" r="2" fill={p.control} /></g>
        </svg>
      </div>
      <div role="status" aria-live="polite" aria-atomic="true">
        <h2 className="maps-loading-title">Exploring {location}</h2>
        <p className="maps-loading-note">{slow ? 'Still searching. Some places take a little longer.' : 'Finding places worth exploring.'}</p>
      </div>
    </div>
    <div className="maps-loading-cards" aria-hidden="true">
      {[0, 1, 2].map(index => <div className="maps-loading-card" key={index}>
        <div className="maps-loading-photo" />
        <div className="maps-loading-lines"><div className="maps-loading-line" /><div className="maps-loading-line" /><div className="maps-loading-line" /></div>
      </div>)}
    </div>
  </section>;
}
